/**
 * Central module configuration for the platform
 * Defines all available modules, their routes, icons, and permission requirements
 */

import { 
  LayoutDashboard, 
  Shield, 
  PenTool, 
  ClipboardList,
  Wrench,
  FolderOpen,
  TrendingUp,
  FileText,
  MessageCircle,
  Bot,
  Package,
  BarChart3,
  MapPin,
  Radio,
  type LucideIcon
} from 'lucide-react';

export interface ModuleDefinition {
  id: string;
  name: string;
  description: string;
  icon: LucideIcon;
  route: string;
  /** If true, only admins can access */
  requiresAdmin?: boolean;
  /** If set, checks this feature flag key */
  featureFlag?: string;
  /** If true, module is coming soon and not clickable */
  comingSoon?: boolean;
  /**
   * Full external URL when the module is deployed as a standalone app.
   * When set, the ModuleSwitcher uses window.location.href instead of navigate().
   * Resolved at runtime from VITE_*_URL env vars.
   */
  externalUrl?: string;
}

/**
 * Active modules that users can navigate to
 */
// Base URL for the root/admin app — used to build externalUrl for modules
// that don't have their own standalone deployment.
const ADMIN_BASE = import.meta.env.VITE_ADMIN_URL || '';

export const ACTIVE_MODULES: ModuleDefinition[] = [
  {
    id: 'portal',
    name: 'Client Portal',
    description: 'Tasks, documents, and settings',
    icon: LayoutDashboard,
    route: '/portal',
    externalUrl: ADMIN_BASE ? `${ADMIN_BASE}/portal` : undefined,
  },
  {
    id: 'admin',
    name: 'Admin',
    description: 'Service hub and administration',
    icon: Shield,
    route: '/admin',
    requiresAdmin: true,
    externalUrl: ADMIN_BASE ? `${ADMIN_BASE}/admin` : undefined,
  },
  {
    id: 'signatures',
    name: 'E-Signatures',
    description: 'Document signing management',
    icon: PenTool,
    route: '/signatures',
    externalUrl: ADMIN_BASE ? `${ADMIN_BASE}/signatures` : undefined,
  },
  {
    id: 'helpdesk',
    name: 'Helpdesk',
    description: 'Support ticket management',
    icon: MessageCircle,
    route: '/admin/helpdesk',
    requiresAdmin: true,
    externalUrl: ADMIN_BASE ? `${ADMIN_BASE}/admin/helpdesk` : undefined,
  },
  {
    id: 'onboarding',
    name: 'Onboarding Hub',
    description: 'Client onboarding workflows',
    icon: ClipboardList,
    route: '/admin/onboarding-page',
    requiresAdmin: true,
    externalUrl: import.meta.env.VITE_EVERBOARDING_URL || undefined,
  },
  {
    id: 'agents',
    name: 'Agent Hub',
    description: 'AI agent management & oversight',
    icon: Bot,
    route: '/admin/agent-command-center',
    requiresAdmin: true,
    externalUrl: ADMIN_BASE ? `${ADMIN_BASE}/admin/agent-command-center` : undefined,
  },
  {
    id: 'assets',
    name: 'Asset Management',
    description: 'Assets, territory, partners & products',
    icon: Package,
    route: '/assets',
    externalUrl: ADMIN_BASE ? `${ADMIN_BASE}/assets` : undefined,
  },
  {
    id: 'cmms',
    name: 'CMMS',
    description: 'Work orders, maintenance & field ops',
    icon: Wrench,
    route: '/cmms',
    externalUrl: import.meta.env.VITE_CMMS_URL || undefined,
  },
  {
    id: 'dms',
    name: 'Documents',
    description: 'Document management & compliance',
    icon: FolderOpen,
    route: '/dms',
    externalUrl: ADMIN_BASE ? `${ADMIN_BASE}/dms` : undefined,
  },
  {
    id: 'analytics',
    name: 'Analytics',
    description: 'Cross-module intelligence & reporting',
    icon: BarChart3,
    route: '/analytics',
    externalUrl: ADMIN_BASE ? `${ADMIN_BASE}/analytics` : undefined,
  },
  {
    id: 'site-dev',
    name: 'Site Development',
    description: 'Site requests, assessments & planning applications',
    icon: MapPin,
    route: '/site-dev',
    externalUrl: ADMIN_BASE ? `${ADMIN_BASE}/site-dev` : undefined,
  },
  {
    id: 'comms',
    name: 'Communications',
    description: 'Unified communications hub',
    icon: Radio,
    route: '/comms',
    externalUrl: import.meta.env.VITE_COMMS_URL || undefined,
  },
];

/**
 * Future modules shown as "Coming Soon"
 */
export const COMING_SOON_MODULES: ModuleDefinition[] = [
  {
    id: 'sales',
    name: 'Sales',
    description: 'Sales pipeline and CRM',
    icon: TrendingUp,
    route: '',
    comingSoon: true,
  },
  {
    id: 'cms',
    name: 'Content',
    description: 'Content management system',
    icon: FileText,
    route: '',
    comingSoon: true,
  },
];

/**
 * All modules combined
 */
export const ALL_MODULES = [...ACTIVE_MODULES, ...COMING_SOON_MODULES];

/**
 * Get module by ID
 */
export const getModuleById = (id: string): ModuleDefinition | undefined => {
  return ALL_MODULES.find(m => m.id === id);
};

/**
 * Get current module based on pathname
 */
export const getCurrentModule = (pathname: string): ModuleDefinition | undefined => {
  const sortedModules = [...ACTIVE_MODULES].sort((a, b) => b.route.length - a.route.length);
  
  for (const module of sortedModules) {
    if (pathname.startsWith(module.route)) {
      return module;
    }
  }
  
  return undefined;
};
